import { useMemo } from "react";
import {
  DataTableSortByDropdown,
  Menu,
  MenuCheckboxItem,
  MenuPopup,
  MenuTrigger,
  OPTION_PANEL_SECTION_INSET,
  OptionPanelNavRow,
  OptionPanelSection,
  type DataTableGroupSortDirection,
  type IconName,
} from "@salestrak/ui";
import { OptionsPanelShell } from "#app/components/view-options/options-panel-shell";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import {
  ASSIGNED_SORT_LABEL,
  assignedSortDirection,
  type AssignedSort,
} from "./use-assigned-list";

/** The saved settings the card's Customize view edits — the card's config
 *  keys, one place, plus the per-viewer direction lens. */
export type AssignedCustomizeValue = {
  types: readonly string[];
  sort: AssignedSort;
  sortDirection: DataTableGroupSortDirection;
};

export type AssignedCustomizePage = "main" | "filters";

function directionWord(direction: DataTableGroupSortDirection): string {
  return direction === "asc" ? "ascending" : "descending";
}

function listWords(words: readonly string[], all: string): string {
  if (words.length === 0) return all;
  if (words.length <= 2) return words.join(", ");
  return `${words[0]} +${words.length - 1}`;
}

/**
 * A filter row: the Filters page's grammar — a nav row whose value reads what
 * is on, opening its values BESIDE the panel as checks that do not close the
 * menu, so picking three is one visit.
 */
function FilterRow<T extends string>({
  label,
  icon,
  options,
  value,
  onChange,
  all,
  disabled,
}: {
  label: string;
  icon: IconName;
  options: readonly { value: T; label: string }[];
  /** The values that are on; empty means every value. */
  value: readonly T[];
  onChange: (next: T[]) => void;
  /** The readout when nothing narrows. */
  all: string;
  disabled: boolean;
}) {
  const on = new Set(value);
  // Every value on reads as the unnarrowed state, not as a list of them all.
  const readout =
    on.size >= options.length
      ? all
      : listWords(
          options.filter((option) => on.has(option.value)).map((option) => option.label),
          all,
        );
  return (
    <Menu modal={false}>
      <MenuTrigger
        disabled={disabled}
        render={<OptionPanelNavRow disabled={disabled} icon={icon} label={label} value={readout} />}
      />
      <MenuPopup align="start" side="inline-end" sideOffset={OPTION_PANEL_SECTION_INSET} width="panel">
        {options.map((option) => {
          const checked = on.has(option.value);
          return (
            <MenuCheckboxItem
              checked={checked}
              key={option.value}
              onCheckedChange={(next) =>
                onChange(
                  next
                    ? options.filter((o) => o.value === option.value || on.has(o.value)).map((o) => o.value)
                    : value.filter((v) => v !== option.value),
                )
              }
              variant="trailing"
            >
              {option.label}
            </MenuCheckboxItem>
          );
        })}
      </MenuPopup>
    </Menu>
  );
}

/**
 * The card's Customize view: the workflow list's own drawer (the
 * `OptionsPanelShell`, the `OptionPanel` row grammar, the shared Sort by
 * popover) pinned over the card by the kit's card anchor, holding exactly the
 * rows this body offers — Filters and Sort by — and nothing about the card's
 * frame. Every row saves on change: My Work is the member's own board, so the
 * card's config is the view and there is no Save to press.
 *
 * Filters is the project Type, by name. The body is the scoped project table
 * or list, which bands nothing of its own, so there is no Group by row.
 */
export function AssignedCustomizePanel({
  open,
  onOpenChange,
  page,
  onPageChange,
  anchorId,
  value,
  onChange,
  canEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  page: AssignedCustomizePage;
  onPageChange: (page: AssignedCustomizePage) => void;
  anchorId: string;
  value: AssignedCustomizeValue;
  onChange: (patch: Partial<AssignedCustomizeValue>) => void;
  /** Without config write access every row reads but none edits. */
  canEdit: boolean;
}) {
  const close = (next: boolean) => {
    if (!next) onPageChange("main");
    onOpenChange(next);
  };

  const { issueTypes } = useWorkspaceData();
  const typeOptions = useMemo(
    () =>
      [...issueTypes]
        .map((type) => ({ value: type.name, label: type.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [issueTypes],
  );

  const filtersReadout = value.types.length > 0 ? "1 filter" : "None";
  const sortLabel = `${ASSIGNED_SORT_LABEL[value.sort]}, ${directionWord(value.sortDirection)}`;

  if (page === "filters") {
    return (
      <OptionsPanelShell
        anchorId={anchorId}
        frame="card"
        onBack={() => onPageChange("main")}
        onOpenChange={close}
        open={open}
        title="Filters"
      >
        <OptionPanelSection>
          <FilterRow
            all="All types"
            disabled={!canEdit || typeOptions.length === 0}
            icon={ENTITY_KIND_ICON.issueType}
            label="Type"
            onChange={(types) => onChange({ types })}
            options={typeOptions}
            value={value.types}
          />
        </OptionPanelSection>
      </OptionsPanelShell>
    );
  }

  return (
    <OptionsPanelShell
      anchorId={anchorId}
      frame="card"
      onOpenChange={close}
      open={open}
      title="Customize view"
    >
      <OptionPanelSection>
        <OptionPanelNavRow
          icon="filter"
          label="Filters"
          onClick={() => onPageChange("filters")}
          value={filtersReadout}
        />
        <DataTableSortByDropdown
          setSortByField={(field) => {
            const sort: AssignedSort =
              field === "updated" || field === "created" || field === "name" ? field : "updated";
            onChange({ sort, sortDirection: assignedSortDirection(sort) });
          }}
          setSortDirection={(sortDirection) => onChange({ sortDirection })}
          sortByField={value.sort}
          sortByLabels={ASSIGNED_SORT_LABEL}
          sortDirection={value.sortDirection}
          trigger={
            <OptionPanelNavRow disabled={!canEdit} icon="arrow-up-down" label="Sort by" value={sortLabel} />
          }
        />
      </OptionPanelSection>
    </OptionsPanelShell>
  );
}
